import { PATH } from '../Constants/paths';
import { IUser, USER_ROLE } from '../Types/user';
import { getUser } from '../Api/getUser';
import * as authActions from '../Components/Auth/authActions';
import { clearRedirectUrl, getRedirectUrl } from './redirectAfterLogin';
import { consumeMcpDestination } from '../features/mcp/continuation';
import type { Dispatch } from 'redux';
import type { NavigateFunction } from 'react-router';

/**
 * True when an admin has requested first-time billing setup and the effective
 * date is still in the future (client admins can complete setup early).
 */
export function clientHasScheduledBillingSetup(user: IUser): boolean {
  if (user.role === USER_ROLE.ADMIN) {
    return false;
  }
  const client = user.client;
  if (!client?.billing_setup_requested_at) {
    return false;
  }
  const effective = client.billing_setup_effective_at;
  if (!effective) {
    return false;
  }
  return new Date(effective).getTime() > Date.now();
}

/**
 * True when an admin has requested first-time billing setup for the user's
 * client and that request is now in effect (immediate or the effective date has
 * arrived). Platform admins are never gated.
 */
export function clientRequiresBillingSetup(user: IUser): boolean {
  if (user.role === USER_ROLE.ADMIN) {
    return false;
  }
  const client = user.client;
  if (!client?.billing_setup_requested_at) {
    return false;
  }
  const effective = client.billing_setup_effective_at;
  if (!effective) {
    return true;
  }
  return new Date(effective).getTime() <= Date.now();
}

/** True when the user's primary client is locked out (non-admin users only). */
export function isClientServiceSuspended(user: IUser): boolean {
  if (user.role === USER_ROLE.ADMIN) {
    return false;
  }
  if (user.client?.service_enabled === false) {
    return true;
  }
  return clientRequiresBillingSetup(user);
}

/** Post-login destination: inactive page when suspended, otherwise saved redirect or home. */
export function getPostLoginDestination(user: IUser): string {
  if (isClientServiceSuspended(user)) {
    return PATH.SUBSCRIPTION_INACTIVE;
  }
  return getRedirectUrl();
}

/** Resolve the destination before clearing it so sign-in preserves setup links. */
export function consumePostLoginDestination(user: IUser): string {
  const mcpDestination = consumeMcpDestination();
  if (mcpDestination) return mcpDestination;
  const destination = getPostLoginDestination(user);
  clearRedirectUrl();
  return destination;
}

/** Refresh `/users/me` after billing changes and redirect when the org is suspended. */
export function refreshUserAfterBillingChange(
  dispatch: Dispatch,
  navigate?: NavigateFunction
): Promise<IUser | null> {
  return new Promise((resolve) => {
    getUser(
      (freshUser) => {
        dispatch(authActions.userLoaded(freshUser));
        if (navigate && isClientServiceSuspended(freshUser)) {
          navigate(PATH.SUBSCRIPTION_INACTIVE, { replace: true });
        }
        resolve(freshUser);
      },
      () => resolve(null)
    );
  });
}
